<template>
  <div class="acao-objeto-list">
    <Loading :isActive="loading" />

    <AcaoObjetoEdit
      :modal-name="modalName"
      :endpoint-base="endpointBase"
      :item="currentItem"
      :eventos="eventos"
      :acao-tipos="acaoTipos"
      :operadores="operadores"
      :default-entidade="entidade"
      @save="salvar"
      @close="closeEdit"
    />

    <AcaoObjetoTemplatesModal modal-name="acaoObjetoTemplates" @apply="aplicarTemplate" />
    <AcaoObjetoImportarModal modal-name="acaoObjetoImportar" @importar="onImportarJson" @baixar-modelo="baixarModelo" />

    <!-- Header -->
    <div class="aol-header">
      <div class="aol-header__title-wrap">
        <div class="aol-header__icon">
          <fa-icon icon="bolt" />
        </div>
        <div>
          <h4 class="aol-header__title">Gatilhos</h4>
          <p class="aol-header__subtitle">
            Automatize ações em resposta a eventos do sistema. Configure condições, defina disparos e acompanhe sua execução.
          </p>
        </div>
      </div>
      <div class="aol-header__actions">
        <button class="aol-create-btn aol-create-btn--ghost" @click="$modal.show('acaoObjetoTemplates')" title="Criar a partir de um template">
          <fa-icon icon="layer-group" /> Templates
        </button>
        <button class="aol-create-btn aol-create-btn--ghost" @click="$modal.show('acaoObjetoImportar')" title="Importar gatilho a partir de um arquivo JSON">
          <fa-icon icon="upload" /> Importar
        </button>
        <button v-if="canCreate" class="aol-create-btn" @click="openCreate">
          <fa-icon icon="plus" /> Novo gatilho
        </button>
      </div>
    </div>

    <!-- Alerta de falhas -->
    <div v-if="stats.gatilhos_com_falhas_24h" class="aol-alert" @click="filtrarFalhas">
      <fa-icon icon="exclamation-triangle" class="aol-alert__icon" />
      <div>
        <strong>{{ stats.gatilhos_com_falhas_24h }} gatilho(s)</strong> com falhas nas últimas 24 horas.
        <span class="aol-alert__cta">Clique para filtrar</span>
      </div>
    </div>

    <!-- Stats -->
    <div class="aol-stats">
      <div class="aol-stat aol-stat--primary">
        <div class="aol-stat__icon"><fa-icon icon="bolt" /></div>
        <div class="aol-stat__body">
          <div class="aol-stat__value">{{ stats.total }}</div>
          <div class="aol-stat__label">Total cadastrados</div>
        </div>
      </div>
      <div class="aol-stat aol-stat--success">
        <div class="aol-stat__icon"><fa-icon icon="check-circle" /></div>
        <div class="aol-stat__body">
          <div class="aol-stat__value">{{ stats.ativos }}</div>
          <div class="aol-stat__label">Ativos</div>
        </div>
      </div>
      <div class="aol-stat aol-stat--muted">
        <div class="aol-stat__icon"><fa-icon icon="pause-circle" /></div>
        <div class="aol-stat__body">
          <div class="aol-stat__value">{{ stats.inativos }}</div>
          <div class="aol-stat__label">Inativos</div>
        </div>
      </div>
      <div class="aol-stat aol-stat--accent">
        <div class="aol-stat__icon"><fa-icon icon="play-circle" /></div>
        <div class="aol-stat__body">
          <div class="aol-stat__value">{{ stats.execucoes_24h.total }}</div>
          <div class="aol-stat__label">Execuções 24h</div>
          <div class="aol-stat__subline">
            <span class="aol-stat__pill is-success">{{ stats.execucoes_24h.sucesso }} ok</span>
            <span class="aol-stat__pill is-failure" v-if="stats.execucoes_24h.falha">{{ stats.execucoes_24h.falha }} falha</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="aol-filters">
      <div class="aol-filters__row">
        <div class="aol-filters__field aol-filters__field--search">
          <fa-icon icon="search" class="aol-filters__icon" />
          <input
            class="form-control aol-filters__input"
            placeholder="Buscar por nome..."
            v-model="filtros.busca"
            @input="loadDebounced"
          />
        </div>

        <div class="aol-filters__field">
          <label class="aol-filters__label">Evento</label>
          <select class="form-control" v-model="filtros.evento" @change="load()">
            <option :value="''">Todos os eventos</option>
            <optgroup
              v-for="grupo in eventosAgrupados"
              :key="grupo.entidade"
              :label="grupo.label"
            >
              <option v-for="e in grupo.eventos" :key="e.slug" :value="e.slug">{{ e.nome }}</option>
            </optgroup>
          </select>
        </div>

        <div class="aol-filters__field">
          <label class="aol-filters__label">Status</label>
          <select class="form-control" v-model="filtros.ativo" @change="load()">
            <option :value="''">Todos</option>
            <option :value="1">Ativos</option>
            <option :value="0">Inativos</option>
          </select>
        </div>

        <div class="aol-filters__field">
          <label class="aol-filters__label">Tipo de ação</label>
          <select class="form-control" v-model="filtros.acao_tipo_slug" @change="load()">
            <option :value="''">Todas as ações</option>
            <option v-for="t in acaoTipos" :key="t.slug" :value="t.slug">{{ t.nome }}</option>
          </select>
        </div>

        <div class="aol-filters__field">
          <label class="aol-filters__label">Saúde</label>
          <select class="form-control" v-model="filtroSaude" @change="load()">
            <option value="">Todos</option>
            <option value="com_falhas">Com falhas (24h)</option>
            <option value="sem_execucoes">Sem execuções</option>
          </select>
        </div>

        <button v-if="hasFiltros" class="aol-filters__clear" @click="limparFiltros" title="Limpar filtros">
          <fa-icon icon="times" /> Limpar
        </button>
      </div>
    </div>

    <!-- Tabela -->
    <div class="aol-table-wrapper">
      <table class="aol-table">
        <thead>
          <tr>
            <th class="col-id">ID</th>
            <th class="col-nome">Nome</th>
            <th class="col-evento">Evento</th>
            <th class="col-condicao">Condição</th>
            <th class="col-saude">Saúde</th>
            <th class="col-ultima">Última exec.</th>
            <th class="col-status">Status</th>
            <th class="col-acoes"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!itemsArr.length && !loading">
            <td colspan="8" class="aol-empty">
              <fa-icon icon="bolt" class="aol-empty__icon" />
              <p class="aol-empty__title">Nenhum gatilho encontrado</p>
              <p class="aol-empty__hint">Crie seu primeiro gatilho para automatizar ações em resposta a eventos.</p>
              <button v-if="canCreate" class="aol-create-btn aol-create-btn--sm" @click="openCreate">
                <fa-icon icon="plus" /> Novo gatilho
              </button>
            </td>
          </tr>
          <tr v-for="item in itemsArr" :key="item.id" class="aol-row">
            <td class="col-id">{{ item.id }}</td>
            <td class="col-nome">
              <div class="aol-nome">
                <span class="aol-nome__text">{{ item.nome }}</span>
              </div>
              <div v-if="item.descricao" class="aol-nome__desc">{{ item.descricao }}</div>
            </td>
            <td class="col-evento">
              <span class="aol-evento-badge">
                <fa-icon icon="bolt" class="aol-evento-badge__icon" />
                {{ eventoLabel(item.evento) }}
              </span>
            </td>
            <td class="col-condicao">
              <span class="aol-cond-pill" :class="`aol-cond-pill--${item.condition_type || 'visual'}`">
                {{ condicaoLabel(item.condition_type) }}
              </span>
            </td>
            <td class="col-saude">
              <span class="aol-saude" :class="`aol-saude--${item.saude || 'sem_dados'}`" :title="saudeTitle(item)">
                <span class="aol-saude__dot"></span>
                {{ saudeLabel(item.saude) }}
              </span>
            </td>
            <td class="col-ultima">
              <div v-if="item.last_execution" class="aol-ultima">
                <span class="aol-ultima__badge" :class="`is-${item.last_execution.result}`">
                  {{ resultLabel(item.last_execution.result) }}
                </span>
                <span class="aol-ultima__data">{{ formatRelative(item.last_execution.executed_at) }}</span>
              </div>
              <span v-else class="aol-ultima__none">—</span>
            </td>
            <td class="col-status">
              <button
                v-if="canToggle"
                class="aol-status-toggle"
                :class="item.ativo ? 'is-active' : 'is-inactive'"
                @click="toggle(item)"
                :title="item.ativo ? 'Clique para desativar' : 'Clique para ativar'"
              >
                <span class="aol-status-toggle__dot"></span>
                {{ item.ativo ? 'Ativo' : 'Inativo' }}
              </button>
              <span v-else class="aol-status-badge" :class="item.ativo ? 'is-active' : 'is-inactive'">
                {{ item.ativo ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="col-acoes">
              <button v-if="canEdit" type="button" class="aol-icon-btn aol-icon-btn--primary" @click="openEdit(item)" title="Editar">
                <fa-icon icon="pencil-alt" />
              </button>
              <button v-if="canCreate" type="button" class="aol-icon-btn aol-icon-btn--accent" @click="duplicar(item)" title="Duplicar">
                <fa-icon icon="copy" />
              </button>
              <button type="button" class="aol-icon-btn aol-icon-btn--ghost" @click="exportar(item)" title="Exportar JSON">
                <fa-icon icon="download" />
              </button>
              <button v-if="canDelete" type="button" class="aol-icon-btn aol-icon-btn--danger" @click="confirmDelete(item.id)" title="Excluir">
                <fa-icon icon="trash-alt" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="meta.last_page > 1" class="aol-pagination">
      <button class="aol-page-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">
        <fa-icon icon="chevron-left" />
      </button>
      <span class="aol-pagination__info">Página <strong>{{ meta.current_page }}</strong> de <strong>{{ meta.last_page }}</strong></span>
      <button class="aol-page-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">
        <fa-icon icon="chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import { get, post, put, del, patch } from '@/helpers/apiRequest';
import AcaoObjetoEdit from './AcaoObjetoEdit.vue';
import AcaoObjetoTemplatesModal from './AcaoObjetoTemplatesModal.vue';
import AcaoObjetoImportarModal from './AcaoObjetoImportarModal.vue';

const CONDICAO_LABELS = {
  visual: 'Visual',
  sql: 'SQL avançado',
};
const RESULT_LABELS = { success: 'Sucesso', failure: 'Falha', skipped: 'Skip' };
const SAUDE_LABELS = {
  saudavel: 'Saudável',
  alerta: 'Alerta',
  critico: 'Crítico',
  sem_dados: 'Sem dados',
};

export default {
  name: 'AcaoObjetoList',
  components: { AcaoObjetoEdit, AcaoObjetoTemplatesModal, AcaoObjetoImportarModal },
  props: {
    endpointBase: { type: String, required: true },
    entidade: { type: String, default: null },
    entidadesExcluir: { type: Array, default: () => [] },
    permissions: {
      type: Object,
      default: () => ({ create: true, edit: true, delete: true, toggle: true }),
    },
    modalName: { type: String, default: 'acaoObjetoEdit' },
  },
  data() {
    return {
      loading: false,
      items: [],
      meta: { current_page: 1, last_page: 1 },
      filtros: { busca: '', evento: '', ativo: '', acao_tipo_slug: '', com_falhas_24h: '', sem_execucoes: '' },
      filtroSaude: '',
      eventos: [],
      acaoTipos: [],
      operadores: [],
      currentItem: null,
      stats: {
        total: 0, ativos: 0, inativos: 0,
        execucoes_24h: { sucesso: 0, falha: 0, skipped: 0, total: 0 },
        gatilhos_com_falhas_24h: 0,
      },
      _debounceTimer: null,
    };
  },
  computed: {
    canCreate() { return this.permissions.create !== false; },
    canEdit()   { return this.permissions.edit   !== false; },
    canDelete() { return this.permissions.delete !== false; },
    canToggle() { return this.permissions.toggle !== false; },
    itemsArr()  { return Array.isArray(this.items) ? this.items : []; },
    hasFiltros() {
      const f = this.filtros;
      return !!(f.busca || f.evento || f.ativo !== '' || f.acao_tipo_slug || this.filtroSaude);
    },
    eventosAgrupados() {
      const ENTIDADE_ORDEM_LABELS = [
        ['pedido',      'Pedido'],
        ['orcamento',   'Orçamento'],
        ['agenda',      'Agenda'],
        ['cliente',     'Cliente / Lead'],
        ['colaborador', 'Colaborador'],
        ['sac',         'SAC / Ticket'],
        ['atendimento', 'Atendimento'],
        ['pagamento',   'Pagamento'],
        ['outros',      'Outros'],
      ];
      const buckets = {};
      const excluir = (this.entidadesExcluir || []).map(e => String(e).toLowerCase());
      (this.eventos || []).forEach(ev => {
        const key = (ev.entidade || 'outros').toLowerCase();
        if (excluir.includes(key)) return;
        (buckets[key] = buckets[key] || []).push(ev);
      });
      const out = [];
      const usados = new Set();
      ENTIDADE_ORDEM_LABELS.forEach(([key, label]) => {
        if (buckets[key] && buckets[key].length) {
          out.push({ entidade: key, label, eventos: buckets[key] });
          usados.add(key);
        }
      });
      Object.keys(buckets).filter(k => !usados.has(k)).sort().forEach(key => {
        out.push({
          entidade: key,
          label:    key.charAt(0).toUpperCase() + key.slice(1),
          eventos:  buckets[key],
        });
      });
      return out;
    },
  },
  watch: {
    filtroSaude(v) {
      this.filtros.com_falhas_24h = v === 'com_falhas' ? 1 : '';
      this.filtros.sem_execucoes  = v === 'sem_execucoes' ? 1 : '';
    },
  },
  beforeDestroy() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  },
  methods: {
    eventoLabel(slug) {
      const e = this.eventos.find(x => x.slug === slug);
      return e ? e.nome : slug;
    },
    condicaoLabel(type) { return CONDICAO_LABELS[type] || CONDICAO_LABELS.default; },
    resultLabel(r) { return RESULT_LABELS[r] || r; },
    saudeLabel(s) { return SAUDE_LABELS[s] || SAUDE_LABELS.sem_dados; },
    saudeTitle(item) {
      const s24 = item.sucessos_24h || 0;
      const f24 = item.falhas_24h || 0;
      return `Últimas 24h — ${s24} sucesso(s) / ${f24} falha(s)`;
    },
    formatRelative(s) {
      if (!s) return '—';
      try {
        const d = new Date(s);
        const diff = Math.floor((Date.now() - d.getTime()) / 1000);
        if (diff < 60) return 'há instantes';
        if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
        if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
        if (diff < 86400 * 7) return `há ${Math.floor(diff / 86400)}d`;
        return d.toLocaleDateString('pt-BR');
      } catch (_) { return s; }
    },
    load(page = 1) {
      this.loading = true;
      const params = new URLSearchParams();
      if (this.entidade) params.append('entidade', this.entidade);
      (this.entidadesExcluir || []).forEach(e => params.append('entidade_excluir[]', e));
      Object.entries(this.filtros).forEach(([k, v]) => { if (v !== '' && v !== null) params.append(k, v); });
      params.append('page', page);
      get(`${this.endpointBase}?${params.toString()}`)
        .then(json => {
          if (Array.isArray(json)) {
            this.items = json;
            this.meta = { current_page: 1, last_page: 1 };
          } else if (Array.isArray(json && json.data)) {
            this.items = json.data;
            this.meta = json.meta || { current_page: 1, last_page: 1 };
          } else if (Array.isArray(json && json.itens)) {
            this.items = json.itens;
            const p = json.paginator || {};
            this.meta = {
              current_page: p.current_page || p.currentPage || 1,
              last_page: p.last_page || p.lastPage || 1,
            };
          } else {
            this.items = [];
            this.meta = { current_page: 1, last_page: 1 };
          }
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    loadStats() {
      const params = new URLSearchParams();
      if (this.entidade) params.append('entidade', this.entidade);
      (this.entidadesExcluir || []).forEach(e => params.append('entidade_excluir[]', e));
      const qs = params.toString() ? `?${params.toString()}` : '';
      get(`${this.endpointBase}/stats${qs}`)
        .then(json => { this.stats = json; })
        .catch(() => {});
    },
    loadDebounced() {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.load(), 300);
    },
    changePage(p) { this.load(p); },
    limparFiltros() {
      this.filtros = { busca: '', evento: '', ativo: '', acao_tipo_slug: '', com_falhas_24h: '', sem_execucoes: '' };
      this.filtroSaude = '';
      this.load();
    },
    filtrarFalhas() {
      this.filtroSaude = 'com_falhas';
      this.filtros.com_falhas_24h = 1;
      this.filtros.sem_execucoes = '';
      this.load();
    },
    loadMeta() {
      const params = new URLSearchParams();
      if (this.entidade) params.append('entidade', this.entidade);
      (this.entidadesExcluir || []).forEach(e => params.append('entidade_excluir[]', e));
      const qs = params.toString() ? `?${params.toString()}` : '';
      Promise.all([
        get(`${this.endpointBase}/eventos${qs}`),
        get(`${this.endpointBase}/acao-tipos`),
        get(`${this.endpointBase}/operadores`),
      ]).then(([eventos, tipos, operadores]) => {
        const excluir = (this.entidadesExcluir || []).map(e => String(e).toLowerCase());
        this.eventos = excluir.length
          ? (eventos || []).filter(ev => !excluir.includes(String(ev.entidade || '').toLowerCase()))
          : eventos;
        this.acaoTipos = tipos;
        this.operadores = operadores;
      });
    },
    openCreate() {
      this.currentItem = { __new: Date.now() };
      this.$nextTick(() => this.$modal.show(this.modalName));
    },
    openEdit(item) {
      get(`${this.endpointBase}/${item.id}`)
        .then(json => {
          const data = json.data || json;
          this.currentItem = { ...data, __opened: Date.now() };
          this.$nextTick(() => this.$modal.show(this.modalName));
        });
    },
    closeEdit() { this.$modal.hide(this.modalName); },
    salvar(form) {
      const req = form.id
        ? put(`${this.endpointBase}/${form.id}`, form)
        : post(this.endpointBase, form);

      req.then(() => {
        this.closeEdit();
        this.load(this.meta.current_page);
        this.loadStats();
        this.$swal({ title: 'Salvo com sucesso', icon: 'success' });
      }).catch(err => {
        this.$swal({ title: 'Falha ao salvar', text: err.message, icon: 'error' });
      });
    },
    toggle(item) {
      patch(`${this.endpointBase}/${item.id}/toggle`, {})
        .then(() => { this.load(this.meta.current_page); this.loadStats(); })
        .catch(err => this.$swal({ title: 'Falha ao alternar', text: err.message || 'Erro de rede', icon: 'error' }));
    },
    confirmDelete(id) {
      this.$swal({
        title: 'Confirma excluir?',
        showCancelButton: true,
        icon: 'warning',
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (!res.value) return;
        del(`${this.endpointBase}/${id}`)
          .then(() => { this.load(this.meta.current_page); this.loadStats(); this.$swal({ title: 'Excluído', icon: 'success' }); })
          .catch(err => this.$swal({ title: 'Falha', text: err.message, icon: 'error' }));
      });
    },
    duplicar(item) {
      post(`${this.endpointBase}/${item.id}/duplicar`, {})
        .then((json) => {
          this.load(this.meta.current_page);
          this.loadStats();
          this.$swal({ title: 'Duplicado!', text: 'O novo gatilho foi criado inativo.', icon: 'success' });
        })
        .catch(err => this.$swal({ title: 'Falha ao duplicar', text: err.message, icon: 'error' }));
    },
    exportar(item) {
      get(`${this.endpointBase}/${item.id}`)
        .then(json => {
          const data = json.data || json;
          const exportObj = { ...data };
          delete exportObj.id;
          delete exportObj.created_by;
          delete exportObj.updated_by;
          delete exportObj.datahora_criacao;
          delete exportObj.datahora_atualizacao;
          delete exportObj.last_execution;
          delete exportObj.falhas_24h;
          delete exportObj.sucessos_24h;
          delete exportObj.saude;
          (exportObj.regras || []).forEach(r => { delete r.id; delete r.acao_objeto_id; });
          (exportObj.acoes || []).forEach(a => { delete a.id; delete a.acao_objeto_id; });

          const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `gatilho-${item.id}-${(item.nome || 'export').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(err => this.$swal({ title: 'Falha ao exportar', text: err.message, icon: 'error' }));
    },
    baixarModelo() {
      const primeiroEvento = (this.eventos[0] && this.eventos[0].slug) || 'evento-slug';
      const primeiroAcaoTipo = (this.acaoTipos[0] && this.acaoTipos[0].slug) || 'acao-tipo-slug';
      const modelo = {
        nome: 'Nome do gatilho',
        descricao: 'Descrição opcional explicando o que este gatilho faz',
        evento: primeiroEvento,
        ativo: false,
        validar_todas_regras: true,
        condition_type: 'visual',
        condition_visual: {
          grupos: [
            {
              operador_logico: 'AND',
              condicoes: [
                { campo: 'nome_do_campo', operador: 'igual', valor: 'valor_esperado' },
              ],
            },
          ],
        },
        condition_sql: '',
        regras: [],
        acoes: [
          {
            acao_tipo_slug: primeiroAcaoTipo,
            ordem: 1,
            parametros: {},
          },
        ],
      };
      const conteudo = JSON.stringify(modelo, null, 2);
      const blob = new Blob([conteudo], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modelo-gatilho.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onImportarJson(data) {
      if (!data || typeof data !== 'object') {
        this.$swal({ title: 'Arquivo inválido', text: 'O JSON precisa conter um objeto de gatilho.', icon: 'error' });
        return;
      }
      this.aplicarTemplate({ ...data, ativo: false });
    },
    aplicarTemplate(form) {
      this.currentItem = { ...form, id: null, __new: Date.now() };
      this.$nextTick(() => this.$modal.show(this.modalName));
    },
  },
  beforeMount() {
    this.loadMeta();
    this.load();
    this.loadStats();
  },
};
</script>

<style scoped>
.acao-objeto-list { padding: 4px 2px; }


/* ===== HEADER ===== */
.aol-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.aol-header__title-wrap { display: flex; gap: 14px; align-items: center; }
.aol-header__icon {
  width: 44px; height: 44px; border-radius: 10px;
  background: linear-gradient(135deg, #3a3e81 0%, #5b60a6 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(58, 62, 129, 0.25);
  flex-shrink: 0;
}
.aol-header__title { margin: 0; font-size: 20px; font-weight: 700; color: #1d2939; }
.aol-header__subtitle { margin: 2px 0 0; font-size: 12px; color: #667085; max-width: 540px; line-height: 1.4; }
.aol-header__actions { display: flex; gap: 8px; flex-wrap: wrap; }

.aol-create-btn {
  background: #3a3e81; color: #fff;
  border: none; border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  transition: all 0.15s ease;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.2);
}
.aol-create-btn:hover { background: #2f326b; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(58, 62, 129, 0.3); }
.aol-create-btn--ghost {
  background: #fff;
  color: #3a3e81;
  border: 1px solid #dddce9;
  box-shadow: none;
}
.aol-create-btn--ghost:hover { background: #f2f2f8; transform: none; box-shadow: none; }
.aol-create-btn--sm { padding: 8px 14px; font-size: 12px; margin-top: 12px; }

/* Alerta */
.aol-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fef6e6;
  border: 1px solid #fce4b3;
  border-left: 4px solid #f0ad4e;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  cursor: pointer;
  font-size: 13px;
  color: #8a5a00;
  transition: all 0.15s ease;
}
.aol-alert:hover { background: #fdecc8; }
.aol-alert__icon { font-size: 18px; color: #b85f00; flex-shrink: 0; }
.aol-alert__cta { color: #b85f00; text-decoration: underline; margin-left: 8px; font-weight: 600; }

/* ===== STATS ===== */
.aol-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
@media (max-width: 992px) { .aol-stats { grid-template-columns: repeat(2, 1fr); } }
.aol-stat {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  border-left-width: 4px;
  transition: all 0.15s ease;
}
.aol-stat:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06); }
.aol-stat__icon { width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
.aol-stat__value { font-size: 22px; font-weight: 700; color: #1d2939; line-height: 1; }
.aol-stat__label { font-size: 11px; color: #667085; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
.aol-stat__subline { display: flex; gap: 4px; margin-top: 4px; flex-wrap: wrap; }
.aol-stat__pill { font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 4px; }
.aol-stat__pill.is-success { background: #ecfaee; color: #2e7d32; }
.aol-stat__pill.is-failure { background: #fdecea; color: #b3261e; }
.aol-stat--primary { border-left-color: #3a3e81; }
.aol-stat--primary .aol-stat__icon { background: #f2f2f8; color: #3a3e81; }
.aol-stat--success { border-left-color: #5eb95e; }
.aol-stat--success .aol-stat__icon { background: #ecfaee; color: #5eb95e; }
.aol-stat--muted { border-left-color: #98a2b3; }
.aol-stat--muted .aol-stat__icon { background: #f2f4f7; color: #667085; }
.aol-stat--accent { border-left-color: #2298a3; }
.aol-stat--accent .aol-stat__icon { background: #edf8f9; color: #2298a3; }

/* ===== FILTROS ===== */
.aol-filters {
  background: #f9fafb; border: 1px solid #e4e7eb; border-radius: 10px;
  padding: 14px 16px; margin-bottom: 16px;
}
.aol-filters__row {
  display: grid;
  grid-template-columns: 1.6fr 1.2fr 0.9fr 1.2fr 1fr 1.1fr auto;
  gap: 12px; align-items: end;
}
@media (max-width: 1200px) { .aol-filters__row { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px)  { .aol-filters__row { grid-template-columns: 1fr 1fr; } }
.aol-filters__field { display: flex; flex-direction: column; }
.aol-filters__field--search { position: relative; }
.aol-filters__label { font-size: 11px; color: #667085; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; margin-bottom: 6px; }
.aol-filters__icon { position: absolute; left: 12px; bottom: 11px; color: #98a2b3; pointer-events: none; }
.aol-filters__input { padding-left: 34px !important; }
.aol-filters .form-control { border-radius: 6px; border: 1px solid #dddce9; font-size: 13px; background: #fff; transition: all 0.15s ease; }
.aol-filters .form-control:focus { border-color: #3a3e81; box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1); }
.aol-filters__clear {
  background: #fff; border: 1px solid #dddce9; color: #667085;
  border-radius: 6px; padding: 8px 14px; font-size: 12px; font-weight: 600;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px; height: 38px;
}
.aol-filters__clear:hover { border-color: #d9534f; color: #d9534f; background: #fff5f5; }

/* ===== TABLE ===== */
.aol-table-wrapper { background: #fff; border: 1px solid #e4e7eb; border-radius: 10px; overflow: hidden; margin-bottom: 16px; }
.aol-table { width: 100%; border-collapse: collapse; }
.aol-table thead tr { background: #f9fafb; border-bottom: 1px solid #e4e7eb; }
.aol-table th { font-size: 11px; font-weight: 600; color: #667085; text-transform: uppercase; letter-spacing: 0.4px; padding: 12px 14px; white-space: nowrap; text-align: left; }
.aol-table td { padding: 14px; border-bottom: 1px solid #f2f4f7; font-size: 13px; color: #344054; vertical-align: middle; }
.aol-row:last-child td { border-bottom: none; }
.aol-row:hover { background: #fafbfc; }

.col-id { width: 60px; text-align: center; color: #98a2b3; font-weight: 600; }
.col-nome { min-width: 220px; }
.col-evento { width: 180px; }
.col-condicao { width: 110px; }
.col-saude { width: 110px; }
.col-ultima { width: 160px; }
.col-status { width: 110px; }
.col-acoes { width: 170px; text-align: right; }

.aol-nome { display: flex; align-items: center; gap: 8px; }
.aol-nome__text { font-weight: 600; color: #1d2939; }
.aol-nome__desc { font-size: 11px; color: #98a2b3; margin-top: 2px; line-height: 1.4; }
.aol-tag { display: inline-block; padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; }
.aol-tag--default { background: #f2f2f8; color: #3a3e81; border: 1px solid #dddce9; }

.aol-evento-badge { display: inline-flex; align-items: center; gap: 6px; background: #edf8f9; color: #2298a3; border: 1px solid #c5e6e9; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
.aol-evento-badge__icon { font-size: 9px; }

.aol-cond-pill { display: inline-block; padding: 3px 9px; border-radius: 4px; font-size: 11px; font-weight: 600; }
.aol-cond-pill--visual { background: #f2f2f8; color: #3a3e81; border: 1px solid #dddce9; }
.aol-cond-pill--sql { background: #fef6e6; color: #b85f00; border: 1px solid #fce4b3; }

.aol-saude { display: inline-flex; align-items: center; gap: 6px; padding: 3px 9px; border-radius: 4px; font-size: 11px; font-weight: 600; border: 1px solid; }
.aol-saude__dot { width: 8px; height: 8px; border-radius: 50%; }
.aol-saude--saudavel { background: #ecfaee; color: #2e7d32; border-color: #b9e7be; }
.aol-saude--saudavel .aol-saude__dot { background: #5eb95e; box-shadow: 0 0 0 3px rgba(94, 185, 94, 0.18); }
.aol-saude--alerta { background: #fef6e6; color: #b85f00; border-color: #fce4b3; }
.aol-saude--alerta .aol-saude__dot { background: #f0ad4e; }
.aol-saude--critico { background: #fdecea; color: #b3261e; border-color: #e89189; }
.aol-saude--critico .aol-saude__dot { background: #d9534f; box-shadow: 0 0 0 3px rgba(217, 83, 79, 0.18); }
.aol-saude--sem_dados { background: #f2f4f7; color: #98a2b3; border-color: #e4e7eb; }
.aol-saude--sem_dados .aol-saude__dot { background: #d0d5dd; }

.aol-ultima { display: flex; flex-direction: column; gap: 2px; }
.aol-ultima__badge { font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 4px; align-self: flex-start; text-transform: uppercase; letter-spacing: 0.4px; }
.aol-ultima__badge.is-success { background: #ecfaee; color: #2e7d32; }
.aol-ultima__badge.is-failure { background: #fdecea; color: #b3261e; }
.aol-ultima__badge.is-skipped { background: #f2f4f7; color: #475467; }
.aol-ultima__data { font-size: 11px; color: #98a2b3; }
.aol-ultima__none { color: #cfd5dd; }

/* Status toggle */
.aol-status-toggle, .aol-status-badge {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 4px 11px; border-radius: 12px;
  font-size: 11px; font-weight: 600;
  border: 1px solid; background: #fff;
  cursor: pointer; transition: all 0.15s ease; font-family: inherit;
}
.aol-status-toggle__dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.aol-status-toggle.is-active, .aol-status-badge.is-active { background: #ecfaee; color: #2e7d32; border-color: #b9e7be; }
.aol-status-toggle.is-active .aol-status-toggle__dot { background: #5eb95e; box-shadow: 0 0 0 3px rgba(94, 185, 94, 0.18); }
.aol-status-toggle.is-inactive, .aol-status-badge.is-inactive { background: #f2f4f7; color: #667085; border-color: #e4e7eb; }
.aol-status-toggle.is-inactive .aol-status-toggle__dot { background: #98a2b3; }
.aol-status-toggle:hover { transform: scale(1.04); }

/* Ações */
.col-acoes { white-space: nowrap; }
.aol-icon-btn {
  width: 30px; height: 30px; border-radius: 6px; border: 1px solid;
  background: #fff; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  margin-left: 4px; transition: all 0.15s ease; font-size: 11px;
}
.aol-icon-btn--primary { border-color: #dddce9; color: #3a3e81; }
.aol-icon-btn--primary:hover { background: #3a3e81; color: #fff; border-color: #3a3e81; }
.aol-icon-btn--accent { border-color: #c5e6e9; color: #2298a3; }
.aol-icon-btn--accent:hover { background: #2298a3; color: #fff; border-color: #2298a3; }
.aol-icon-btn--ghost { border-color: #e4e7eb; color: #667085; }
.aol-icon-btn--ghost:hover { background: #f9fafb; color: #1d2939; }
.aol-icon-btn--danger { border-color: #fbd7d6; color: #d9534f; }
.aol-icon-btn--danger:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

/* Empty state */
.aol-empty { text-align: center !important; padding: 48px 20px !important; color: #98a2b3; }
.aol-empty__icon { font-size: 32px; color: #d0d5dd; margin-bottom: 12px; opacity: 0.6; }
.aol-empty__title { font-size: 14px; font-weight: 600; color: #475467; margin: 8px 0 4px; }
.aol-empty__hint { font-size: 12px; color: #98a2b3; margin: 0; }

/* Pagination */
.aol-pagination { display: flex; justify-content: center; align-items: center; gap: 12px; }
.aol-page-btn {
  width: 34px; height: 34px; border-radius: 8px;
  border: 1px solid #dddce9; background: #fff; color: #475467;
  cursor: pointer; transition: all 0.15s ease;
}
.aol-page-btn:hover:not(:disabled) { border-color: #3a3e81; color: #3a3e81; background: #f7f7fc; }
.aol-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.aol-pagination__info { font-size: 12px; color: #667085; }
.aol-pagination__info strong { color: #1d2939; }
</style>
